import { entityTypes, posthogEvents } from '@/shared/constants/constants';
import { posthogLog } from '@/shared/pinia/modules/posthog/logger';
import { useProductStore } from '@/shared/pinia/modules/product';
import { useProductOptionsStore } from '@/shared/pinia/modules/product-options';
import { getProductDataFromHTML } from '@/shared/utils/product';

type ProductViewedData = {
  currency_code: string;
  currency_rate: number;
  product_group_id: string;
  product_id: string;
  product_price: number;
  product_sku: string;
  product_title: string;
  product_type: string;
  product_url: string;
  product_variant: string;
  product_vendor: string;
  bulk_discounts_exists: boolean;
  discount_exists: boolean;
  discount: number;
  inventory_count: number;
  variant_id: number | null;
  variant_source: 'url' | 'none';
  in_stock?: boolean;
  product_in_stock: boolean | null;
  variants_in_stock: number | null;
  variants_total: number | null;
  related_pattern_id?: number;
};

const getUrlVariant = (options: HydratedShopifyVariant[]) => {
  const variantParam = new URLSearchParams(window.location.search).get(
    'variant'
  );
  if (!variantParam) return undefined;
  return options.find((option) => String(option.id) === variantParam);
};

export const trackProductViewed = (isKit: boolean = false) => {
  const {
    productId,
    productName,
    productUniqueSku,
    entityType,
    productPrice: priceFromData,
    productType: typeFromData,
    productVendor: vendorFromData
  } = getProductDataFromHTML();

  const productStore = useProductStore();
  const productOptionsStore = useProductOptionsStore();

  const currencyCode = window.Shopify?.currency?.active || 'USD';
  const currencyRate = window.Hobbii?.hobbiiStoreData?.currencyRate || 1;

  // Get product price from the store or DOM data
  let productPrice = 0;
  if (productStore.price.unit) {
    productPrice = productStore.price.unit / 100;
  } else if (priceFromData) {
    productPrice = parseFloat(priceFromData) / 100;
  } else {
    const priceEl = document.querySelector('[data-price]');
    if (priceEl) {
      const priceData = priceEl.getAttribute('data-price');
      if (priceData) {
        productPrice = parseFloat(priceData) / 100;
      }
    }
  }

  const productGroupId = productStore.resolvedEntityId || '';

  const productType = isKit
    ? entityTypes.KIT
    : typeFromData || entityType || 'product';

  const { options } = productOptionsStore;
  const urlVariant = getUrlVariant(options);
  const hasVariants = options.length > 0;
  const variantsInStock = productOptionsStore.optionsInStock.length;

  const inventoryCount =
    (urlVariant ?? productOptionsStore.targetVariant)?.stockData ?? 0;

  const quantitySelector = document.getElementById(
    'js-product-quantity-selector'
  );
  const discountsData = quantitySelector?.getAttribute('data-discounts');
  const bulkDiscountsExists = !!(
    discountsData &&
    discountsData.trim() !== '' &&
    discountsData !== '[]' &&
    discountsData !== 'null'
  );

  const dataPrice = quantitySelector?.getAttribute('data-price');
  const dataCompareAtPrice = quantitySelector?.getAttribute(
    'data-compare-at-price'
  );

  let discountExists = false;
  let discountPercentage = 0;

  if (dataPrice && dataCompareAtPrice) {
    const price = parseFloat(dataPrice);
    const comparePrice = parseFloat(dataCompareAtPrice);

    if (price < comparePrice) {
      discountExists = true;
      discountPercentage = Math.round(
        ((comparePrice - price) / comparePrice) * 100
      );
    }
  }

  const eventData: ProductViewedData = {
    currency_code: currencyCode,
    currency_rate: currencyRate,
    product_group_id: productGroupId,
    product_id: productId || '',
    product_price: productPrice,
    product_sku: urlVariant?.sku ?? productUniqueSku ?? '',
    product_title: productName || '',
    product_type: productType,
    product_url: window.location.href,
    product_variant: urlVariant?.title ?? '',
    product_vendor: vendorFromData || '',
    bulk_discounts_exists: bulkDiscountsExists,
    discount_exists: discountExists,
    discount: discountPercentage,
    inventory_count: inventoryCount,
    variant_id: urlVariant?.id ?? null,
    variant_source: urlVariant ? 'url' : 'none',
    ...(urlVariant ? { in_stock: urlVariant.inStock } : {}),
    product_in_stock: hasVariants ? variantsInStock > 0 : null,
    variants_in_stock: hasVariants ? variantsInStock : null,
    variants_total: hasVariants ? options.length : null,
    ...(isKit && productStore.relatedPattern?.id
      ? { related_pattern_id: productStore.relatedPattern.id }
      : {})
  };

  posthogLog('Tracking product viewed event:', eventData);

  window.posthog?.capture(posthogEvents.PRODUCT_VIEWED, eventData);
};
